import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import {
  linkForgeAccount,
  listForgeHosts,
  listMyForgeAccounts,
  startForgeOauth,
  unlinkForgeAccount,
} from '@/lib/api/generated/api';
import type { ForgeProvider } from '@/lib/api/generated/model';
import { expectStatus } from '@/lib/api/client';

import { FORGE_COPY, SIGN_IN_COPY } from '../copy';
import {
  forgeName,
  linkRefusal,
  type LinkRefusalCategory,
} from '../forgeAccounts';
import { navigation } from '../navigation';
import { identityKeys } from '../queryKeys';

/** The service refused the token (422): rejected, missing scopes, unknown host. */
export class ForgeTokenRefused extends Error {
  readonly category: LinkRefusalCategory | null;

  constructor(category: LinkRefusalCategory | null, message: string) {
    super(message);
    this.name = 'ForgeTokenRefused';
    this.category = category;
  }
}

/** The forge could not be asked about the token (502); nothing was linked. */
export class ForgeUnreachable extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ForgeUnreachable';
  }
}

/**
 * The service would not start signing in (spec 161): the host's sign-in was
 * removed (409), or no workspace connects to it any more (404).
 */
export class ForgeSignInRefused extends Error {
  readonly host: string;
  readonly reason: 'unavailable' | 'not-connected';

  constructor(host: string, reason: 'unavailable' | 'not-connected') {
    super(
      reason === 'unavailable'
        ? SIGN_IN_COPY.unavailable(host)
        : SIGN_IN_COPY.notConnected(host),
    );
    this.name = 'ForgeSignInRefused';
    this.host = host;
    this.reason = reason;
  }
}

export function isForgeSignInRefused(
  error: unknown,
): error is ForgeSignInRefused {
  return error instanceof ForgeSignInRefused;
}

export function isForgeTokenRefused(
  error: unknown,
): error is ForgeTokenRefused {
  return error instanceof ForgeTokenRefused;
}

export function isForgeUnreachable(error: unknown): error is ForgeUnreachable {
  return error instanceof ForgeUnreachable;
}

/**
 * The signed-in person's linked forge accounts and the hosts they could link
 * (spec 159): read, link, unlink.
 *
 * A token is passed straight to the service and kept nowhere else — not in the
 * cache, not in a store. Refusals and an unreachable forge come back as typed
 * errors, for the form to say beside the field; anything else is a toast.
 */
export function useForgeAccounts() {
  const queryClient = useQueryClient();

  const accounts = useQuery({
    queryKey: identityKeys.forgeAccounts(),
    queryFn: async ({ signal }) =>
      expectStatus(
        await listMyForgeAccounts({ signal }),
        200,
        FORGE_COPY.loadFailed,
      ),
    retry: false,
  });

  const hosts = useQuery({
    queryKey: identityKeys.forgeHosts(),
    queryFn: async ({ signal }) =>
      expectStatus(
        await listForgeHosts({ signal }),
        200,
        FORGE_COPY.loadFailed,
      ),
    retry: false,
  });

  const refresh = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: identityKeys.forgeAccounts() }),
      queryClient.invalidateQueries({ queryKey: identityKeys.forgeHosts() }),
      // A board's notice says what the person lacks (spec 160): linking ends it.
      queryClient.invalidateQueries({
        queryKey: identityKeys.personalAccountStanding(),
      }),
    ]);

  const link = useMutation({
    mutationFn: async ({
      host,
      provider,
      token,
    }: {
      host: string;
      provider: ForgeProvider;
      token: string;
    }) => {
      const response = await linkForgeAccount(host, { token: token.trim() });
      if (response.status === 200) return response.data;
      if (response.status === 422) {
        const refusal = linkRefusal(response.data);
        throw new ForgeTokenRefused(
          refusal?.category ?? null,
          refusal?.message ?? FORGE_COPY.refused(forgeName(provider)),
        );
      }
      if (response.status === 502) {
        throw new ForgeUnreachable(FORGE_COPY.unreachable(forgeName(provider)));
      }
      throw new Error(FORGE_COPY.linkFailed);
    },
    onSuccess: async (result, { provider }) => {
      await refresh();
      const forge = forgeName(provider);
      toast.success(
        result.warnings.includes('scopes_unknown')
          ? FORGE_COPY.linkedUnchecked(forge)
          : FORGE_COPY.linked(forge),
      );
    },
    onError: (error) => {
      if (isForgeTokenRefused(error) || isForgeUnreachable(error)) return;
      toast.error(FORGE_COPY.linkFailed);
    },
  });

  const unlink = useMutation({
    mutationFn: async ({ host }: { host: string; provider: ForgeProvider }) => {
      const response = await unlinkForgeAccount(host);
      if (response.status !== 204) throw new Error(FORGE_COPY.unlinkFailed);
    },
    onSuccess: async (_result, { provider }) => {
      await refresh();
      toast.success(FORGE_COPY.unlinked(forgeName(provider)));
    },
    onError: () => {
      toast.error(FORGE_COPY.unlinkFailed);
    },
  });

  /**
   * Link by signing in on the forge (spec 161): the service keeps the
   * sign-in's secrets and answers with where to go; the browser goes there.
   * The page is unloading then, so the mutation never settles and the button
   * stays busy — a second click starts nothing.
   */
  const connect = useMutation({
    mutationFn: async ({
      host,
    }: {
      host: string;
      provider: ForgeProvider;
    }): Promise<never> => {
      const response = await startForgeOauth(host);
      if (response.status === 200) {
        navigation.leaveFor(response.data.authorize_url);
        return new Promise<never>(() => undefined);
      }
      if (response.status === 409 || response.status === 404) {
        // Whether the host offers signing in has changed under the page.
        void queryClient.invalidateQueries({
          queryKey: identityKeys.forgeHosts(),
        });
        throw new ForgeSignInRefused(
          host,
          response.status === 409 ? 'unavailable' : 'not-connected',
        );
      }
      throw new Error(SIGN_IN_COPY.failed(host));
    },
    onError: (error, { provider }) => {
      if (isForgeSignInRefused(error)) return;
      toast.error(SIGN_IN_COPY.failed(forgeName(provider)));
    },
  });

  return { accounts, hosts, link, unlink, connect };
}
